import { AppContext } from '@/App';
import { createContext, FunctionComponent, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { isPlatformAllowed, shouldDeferForOneTrust, whenOneTrustReady } from '@showclix-shared-util/oneTrustConsent';
import { ConsentGatedFrameAnalytics, shouldReloadOnRevocation, reloadForConsentRevocation } from '@showclix-shared-util/consentGatedAnalytics';

const ORG_LISTING = 1;

const AnalyticsContext = createContext({});

type AnalyticsPlatform = 'google-analytics-4' | 'google-tag-manager';
type ReportableAnalyticsPlatform = Exclude<AnalyticsPlatform, 'google-tag-manager'>;

interface AnalyticsContextProviderProps {
  analytics: Record<AnalyticsPlatform, any>;
  oneTrustEnabled?: boolean;
  oneTrustExtendedSetup?: boolean;
}

export const AnalyticsContextProvider: FunctionComponent<PropsWithChildren<AnalyticsContextProviderProps>> = ({
  children,
  analytics,
  oneTrustEnabled = false,
  oneTrustExtendedSetup = false,
}) => {
  const embedPort = useRef<MessagePort | null>(null);
  const queue = useRef([]);
  const analyticsGate = useRef(new ConsentGatedFrameAnalytics());
  const initializedGtm = useRef(new Set<string>());
  const pageViewSent = useRef(false);
  const baseEventData = useBaseEventData();

  const isBlocked = (type: string) =>
    shouldDeferForOneTrust(oneTrustEnabled) || !isPlatformAllowed(type, oneTrustExtendedSetup);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    window.addEventListener('OneTrustGroupsUpdated', onConsentChange);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('OneTrustGroupsUpdated', onConsentChange);
    };
  }, []);

  const analyticsKey = JSON.stringify(analytics);
  useEffect(() => {
    applyConsent();
    if (!shouldDeferForOneTrust(oneTrustEnabled)) return;
    return whenOneTrustReady(applyConsent);
  }, [analyticsKey]);

  // Consent-change events can revoke an already-initialized tracker: reload to unload it.
  const onConsentChange = () => {
    if (shouldDeferForOneTrust(oneTrustEnabled)) return;

    if (
      shouldReloadOnRevocation(
        analyticsGate.current,
        analytics,
        oneTrustExtendedSetup,
        initializedGtm.current.size > 0 && isBlocked('google-tag-manager'),
      )
    ) {
      reloadForConsentRevocation();
      return;
    }

    applyConsent();
  };

  const applyConsent = () => {
    // Wait for the async OneTrust SDK before initializing anything for a OneTrust partner.
    if (shouldDeferForOneTrust(oneTrustEnabled)) return;

    initGtm();
    sendAnalyticsData();
    firePageView();
  };

  const initGtm = () => {
    if (isBlocked('google-tag-manager')) return;
    const gtmIds = analytics['google-tag-manager']?.gtm_ids;
    if (!gtmIds) return;
    gtmIds.filter(Boolean).forEach((gtmId) => {
      if (!initializedGtm.current.has(gtmId)) {
        TagManager.initialize({ gtmId });
        initializedGtm.current.add(gtmId);
      }
    });
  };

  const firePageView = () => {
    if (pageViewSent.current) return;
    if (isBlocked('google-analytics-4') && isBlocked('google-tag-manager')) return;
    reportEvent('google-analytics-4')('event', 'orglisting_page_view', baseEventData);
    reportGTM({ event: 'orglisting_page_view', ...baseEventData });
    pageViewSent.current = true;
  };

  const onInit = (e) => {
    embedPort.current = e.ports[0];
    analyticsGate.current.reset();
    sendAnalyticsData();
  };

  const sendAnalyticsData = () => {
    if (shouldDeferForOneTrust(oneTrustEnabled)) return;
    analyticsGate.current.sendDelta(embedPort.current, analytics, oneTrustExtendedSetup);
  };

  const onPing = (e) => {
    const iframe = document.getElementById('analytics-container') as HTMLIFrameElement;

    const message = 'pong';

    iframe.contentWindow.postMessage(message, e.origin);
  };

  const appendToQueue = (action) => {
    queue.current.push(action);
  };

  const emptyQueue = () => {
    if (!embedPort.current) return;

    queue.current.forEach((action) => {
      embedPort.current.postMessage(action);
    });

    queue.current = [];
  };

  const handleMessage = (e) => {
    if (typeof e.data !== 'string') return;

    if (e.data === 'init') {
      onInit(e);
    }

    if (e.data === 'ping') {
      onPing(e);
    }

    if (e.data === 'postInitPlatform') {
      emptyQueue();
    }
  };

  const getIdsFromConfig = (config) => {
    if (typeof config === 'string') {
      return [config];
    }

    if (Array.isArray(config)) {
      return config;
    }

    if (typeof config === 'object' && config.measurement_ids) {
      return config.measurement_ids;
    }

    return [];
  };

  const reportEvent =
    (type: ReportableAnalyticsPlatform) =>
    (...args) => {
      if (isBlocked(type)) {
        return;
      }

      const config = analytics[type];

      if (!config) {
        console.warn(`No config found for analytics type: ${type}`);
        return;
      }

      const measurementIds = getIdsFromConfig(config).filter(Boolean);

      const actions = measurementIds.map((measurementId) => ({
        action: 'report',
        platform: type,
        measurementId,
        data: args,
      }));

      if (!embedPort.current) {
        console.log('Analytics is not initialized yet, appending to queue');
        actions.forEach(appendToQueue);
        return;
      }

      actions.forEach((action) => {
        embedPort.current.postMessage(action);
      });
    };

  const reportGTM = (dataLayer) => {
    if (isBlocked('google-tag-manager')) {
      return;
    }
    TagManager.dataLayer({ dataLayer })
  }

  return <AnalyticsContext.Provider value={{ reportEvent, reportGTM }}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsContextProvider');
  }

  return context;
};

export const useBaseEventData = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useBaseEventData must be used within an AppContext');
  }

  // @ts-ignore
  const { listings, type, isCartMode, slug, view } = context.orgListingConfig;

  return useMemo(() => {
    const eventIds = [];
    const eventNames = [];

    if (listings) {
      listings.forEach((listing) => {
        eventIds.push(listing.event_id);
        eventNames.push(listing.listing_title);
      });
    }
    return {
      org_listing_type: type === ORG_LISTING ? 'org listing' : 'managed listing',
      cart_mode: isCartMode,
      listing_slug: slug,
      event_ids: eventIds,
      event_names: eventNames,
      display_type: view,
    };
  }, [type, isCartMode, slug, listings, view]);
};
